// FE-23 — estado de los vencimientos del vehículo: SOAT, tecnomecánica, seguro y
// cambio de aceite. Derivación pura, sin IO ni React (misma convención que
// `utils/trips.ts`, cuya cabecera dice la moraleja: duplicar estas reglas es como
// acaban divergiendo las superficies).
//
// Hasta esta épica el umbral estaba copiado en tres sitios (`VehiclesPage`,
// `VehicleListItem`, `MonitoringPage`), los tres como `<= 30` **sin piso**: un
// documento YA VENCIDO contaba como "por vencer", y el estado `vencido` no
// existía en ninguna parte. Aquí hay una sola definición.
//
// Reglas que gobiernan el archivo y no se deducen de los tipos:
//
//  1. **Los días se cuentan contra el DÍA LOCAL, no contra un instante.** Una
//     fecha de vencimiento es el día impreso en el papel. `new Date('2026-09-21')`
//     la lee en UTC y a un usuario en Bogotá (UTC-5) el SOAT le vencería el día
//     anterior. Por eso se parsea a mano y se compara con `differenceInCalendarDays`.
//  2. **Los documentos NO tienen porcentaje de vigencia.** No sabemos cuándo se
//     expidió el SOAT, así que "% consumido" sería el número inventado que esta
//     épica viene a matar. Con más de `DUE_SOON_DAYS` por delante no hay barra,
//     solo el texto. Dentro de la ventana, la barra mide LA VENTANA
//     (`(30 - díasRestantes) / 30`), no la vigencia.
//  3. **El aceite SÍ tiene denominador, pero solo en las dimensiones que el usuario
//     registró.** Manda la mayor de las que existan ("lo que ocurra primero").
//     Sin ninguna dimensión medible el estado es `unknown` con su motivo, nunca un
//     porcentaje: un vehículo registrado solo por km cuyo odómetro se reinició NO
//     tiene fallback a tiempo, y suponer que siempre lo hay es el error a evitar.
//  4. **`unknown` no es una alerta.** "Sin datos" no cuenta como vencido ni como
//     por vencer: es la ausencia de la información, no un problema del vehículo.

import { addMonths, differenceInCalendarDays, format, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Vehicle } from '../types/vehicle';

/** Días de aviso previos al vencimiento de un documento. La ÚNICA definición. */
export const DUE_SOON_DAYS = 30;

/**
 * Margen de aviso del aceite, en km. **Fijo**, no proporcional (decisión del dueño,
 * 2026-09-15): no se encoge al reducir el intervalo. Se recorta al intervalo si
 * alguien configura menos de 1.000 km — si no, la barra nacería en ámbar.
 */
export const OIL_DUE_SOON_KM = 1000;

export type MaintenanceStatus = 'expired' | 'due_soon' | 'valid' | 'unknown';
export type MaintenanceKey = 'soat' | 'tecnomecanica' | 'insurance' | 'oil';

export const MAINTENANCE_STATUS_LABELS: Record<MaintenanceStatus, string> = {
  expired: 'Vencido',
  due_soon: 'Por vencer',
  valid: 'Vigente',
  unknown: 'Sin datos',
};

/** Qué tan grave es cada estado, para elegir "el peor". `unknown` va al fondo a propósito. */
const RANK: Record<MaintenanceStatus, number> = { expired: 3, due_soon: 2, valid: 1, unknown: 0 };

export interface MaintenanceItem {
  key: MaintenanceKey;
  label: string;
  status: MaintenanceStatus;
  /**
   * 0–1, o `undefined` = **no se pinta barra**, solo el texto. Un documento
   * vigente con más de `DUE_SOON_DAYS` por delante no tiene barra: no hay
   * denominador honesto.
   */
  ratio?: number;
  /** El texto corto que acompaña a la barra ("Vence en 20 días", "Faltan 1.200 km"). */
  caption: string;
  /** Explicación más larga; hoy solo la llevan los `unknown` del aceite. */
  hint?: string;
  /** Días que faltan (negativo = vencido). Solo lo usa `mostUrgent` para desempatar. */
  daysLeft?: number;
}

export interface VehicleMaintenance {
  /** El peor estado entre los cuatro. `unknown` solo si NINGUNO tiene datos. */
  worst: MaintenanceStatus;
  /** Los cuatro, siempre y en este orden: SOAT, tecnomecánica, seguro, aceite. */
  items: MaintenanceItem[];
  /** El ítem que la tarjeta muestra: el más grave; a igual gravedad, el que vence antes. */
  mostUrgent: MaintenanceItem;
  /** Cuántos ítems tienen algún dato (no `unknown`). 0 = el vehículo no tiene nada registrado. */
  withData: number;
}

// ---------------------------------------------------------------------------
// Utilidades de fecha
// ---------------------------------------------------------------------------

/**
 * `YYYY-MM-DD` → medianoche LOCAL de ese día. Deliberadamente no `new Date(str)`
 * (ver la regla 1 de la cabecera). Si llega con hora, se toma solo la parte del día.
 */
export function parseDay(iso: string | undefined | null): Date | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toDayString(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function formatDay(d: Date): string {
  return format(d, 'd MMM yyyy', { locale: es });
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

const formatKmAmount = (km: number) =>
  `${Math.round(km).toLocaleString('es-CO', { maximumFractionDigits: 0 })} km`;

// ---------------------------------------------------------------------------
// Documentos
// ---------------------------------------------------------------------------

const unknownItem = (key: MaintenanceKey, label: string, hint?: string): MaintenanceItem => ({
  key, label, status: 'unknown', caption: 'Sin datos', hint,
});

export function expiryItem(
  key: MaintenanceKey,
  label: string,
  expiresOn: string | undefined,
  today: Date,
): MaintenanceItem {
  const expires = parseDay(expiresOn);
  if (!expires) return unknownItem(key, label);

  const days = differenceInCalendarDays(expires, today);

  if (days < 0) {
    return { key, label, status: 'expired', ratio: 1, daysLeft: days, caption: `Vencido hace ${plural(-days, 'día', 'días')}` };
  }
  if (days <= DUE_SOON_DAYS) {
    return {
      key, label, status: 'due_soon', daysLeft: days,
      // La barra mide la VENTANA de aviso, no la vigencia (regla 2).
      ratio: (DUE_SOON_DAYS - days) / DUE_SOON_DAYS,
      caption: days === 0 ? 'Vence hoy' : `Vence en ${plural(days, 'día', 'días')}`,
    };
  }
  return {
    key, label, status: 'valid', daysLeft: days,
    caption: `Vigente · vence el ${formatDay(expires)} (${days} días)`,
  };
}

// ---------------------------------------------------------------------------
// Aceite
// ---------------------------------------------------------------------------

interface OilDimension {
  status: MaintenanceStatus;
  ratio: number;
  caption: string;
  daysLeft?: number;
}

/** El cambio de aceite vence por lo que ocurra primero: km, tiempo o ambos. */
export function oilChangeItem(oil: Vehicle['oilChange'], today: Date): MaintenanceItem {
  const key: MaintenanceKey = 'oil';
  const label = 'Cambio de aceite';
  const dims: OilDimension[] = [];

  // Kilómetros: SOLO con base medida. Con `RESET`/`UNAVAILABLE` el consumo no se
  // conoce, y un 0 se leería "recién cambiado".
  if (
    oil?.kmBasis === 'MEASURED'
    && oil.intervalKm && oil.intervalKm > 0
    && typeof oil.kmSinceLastChange === 'number'
  ) {
    const remaining = oil.intervalKm - oil.kmSinceLastChange;
    const margin = Math.min(OIL_DUE_SOON_KM, oil.intervalKm);
    dims.push({
      ratio: oil.kmSinceLastChange / oil.intervalKm,
      // `<`, no `<=`: con el margen recortado al intervalo, un aceite recién
      // cambiado (remaining === intervalo === margen) nacería ámbar.
      status: remaining <= 0 ? 'expired' : remaining < margin ? 'due_soon' : 'valid',
      caption: remaining > 0
        ? `Faltan ${formatKmAmount(remaining)}`
        : remaining === 0 ? 'Toca cambiarlo ya' : `Pasado por ${formatKmAmount(-remaining)}`,
    });
  }

  // Tiempo: solo si hay intervalo en meses Y fecha del último cambio.
  const lastOn = parseDay(oil?.lastOn);
  if (lastOn && oil?.intervalMonths && oil.intervalMonths > 0) {
    const due = addMonths(lastOn, oil.intervalMonths);
    const total = differenceInCalendarDays(due, lastOn);
    const elapsed = differenceInCalendarDays(today, lastOn);
    const remainingDays = differenceInCalendarDays(due, today);
    dims.push({
      ratio: total > 0 ? elapsed / total : 1,
      status: remainingDays <= 0 ? 'expired' : remainingDays <= DUE_SOON_DAYS ? 'due_soon' : 'valid',
      daysLeft: remainingDays,
      caption: remainingDays > 0
        ? `Faltan ${plural(remainingDays, 'día', 'días')}`
        : remainingDays === 0 ? 'Toca cambiarlo hoy' : `Pasado por ${plural(-remainingDays, 'día', 'días')}`,
    });
  }

  if (dims.length === 0) return unknownItem(key, label, oilUnknownHint(oil));

  // Manda la más grave; a igual gravedad, la más consumida ("lo que ocurra primero").
  const binding = dims.reduce((a, b) => (
    RANK[b.status] > RANK[a.status] || (RANK[b.status] === RANK[a.status] && b.ratio > a.ratio) ? b : a
  ));

  return {
    key, label,
    status: binding.status,
    ratio: Math.min(Math.max(binding.ratio, 0), 1),
    caption: binding.caption,
    daysLeft: dims.find(d => d.daysLeft !== undefined)?.daysLeft,
  };
}

/** Por qué no se puede medir el aceite. Un vehículo sin ningún registro NO tiene "10.000 supuestos". */
function oilUnknownHint(oil: Vehicle['oilChange']): string {
  const registered = oil && (oil.lastOn || oil.lastKm !== undefined || oil.intervalKm || oil.intervalMonths);
  if (!registered) return 'Registra el cambio de aceite para empezar a medirlo.';
  if (oil?.kmBasis === 'RESET') {
    return 'El odómetro del equipo se reinició: registra el cambio de nuevo para volver a medirlo.';
  }
  if (oil?.kmBasis === 'UNAVAILABLE') {
    return 'El equipo no reporta odómetro, así que no se puede medir por kilómetros.';
  }
  return 'Faltan datos para medirlo: registra el cambio de aceite de nuevo.';
}

// ---------------------------------------------------------------------------
// Vehículo completo
// ---------------------------------------------------------------------------

export function vehicleMaintenance(
  v: Pick<Vehicle, 'documents' | 'oilChange'>,
  now: Date = new Date(),
): VehicleMaintenance {
  const today = startOfDay(now);
  const items: MaintenanceItem[] = [
    expiryItem('soat', 'SOAT', v.documents?.soatExpiresOn, today),
    expiryItem('tecnomecanica', 'Tecnomecánica', v.documents?.tecnomecanicaExpiresOn, today),
    expiryItem('insurance', 'Seguro', v.documents?.insuranceExpiresOn, today),
    oilChangeItem(v.oilChange, today),
  ];

  const byUrgency = [...items].sort((a, b) => (
    RANK[b.status] - RANK[a.status]
    || (a.daysLeft ?? Number.POSITIVE_INFINITY) - (b.daysLeft ?? Number.POSITIVE_INFINITY)
  ));

  return {
    worst: byUrgency[0].status,
    items,
    mostUrgent: byUrgency[0],
    withData: items.filter(i => i.status !== 'unknown').length,
  };
}

// ---------------------------------------------------------------------------
// Atajos de renovación (+6 / +12 / +24 meses)
// ---------------------------------------------------------------------------

/**
 * La fecha que propone un atajo `+Nm` al renovar un documento.
 *
 * Parte del vencimiento ANTERIOR si todavía no llegó: renovar un mes antes de
 * tiempo no corre la fecha — un SOAT renovado con un mes de anticipación sigue
 * venciendo el año siguiente en la fecha del anterior, y guardar "hoy + 12"
 * habría dejado la fecha corrida respecto al papel.
 *
 * Si el anterior ya venció (renovación tardía), parte de HOY: la póliza nueva no
 * puede empezar en el pasado. Esto afina la regla de la ficha ("desde el
 * vencimiento anterior si existe"), que para un SOAT vencido hace dos años
 * propondría una fecha que ya pasó. El atajo solo ahorra la cuenta: el valor
 * que se guarda sigue siendo la fecha impresa en el papel, siempre editable.
 */
export function renewalDate(
  previousExpiresOn: string | undefined,
  months: number,
  now: Date = new Date(),
): string {
  const today = startOfDay(now);
  const previous = parseDay(previousExpiresOn);
  const base = previous && previous >= today ? previous : today;
  return toDayString(addMonths(base, months));
}
